(() => {
  // Total scroll distance to transition from 0% (expanded) to 100% (compact)
  const SCROLL_DISTANCE = 140;

  class VorneelLogoMotion {
    constructor(header) {
      this.header = header;
      this.headerGroup = header.closest('#header-group');

      this.announcement = document.querySelector(
        "#header-group .announcement-bar, #header-group [id^='announcement-bar-'], #header-group [id^='marquee-'], #header-group .marquee-section"
      );
      this.announcementSection = this.announcement ? this.announcement.closest('.shopify-section') : null;
      if (this.announcementSection) {
        this.announcementSection.classList.add('vorneel-announcement-section');
      }

      this.spacer = document.querySelector('#vorneel-header-spacer');

      this.targetProgress = 0;
      this.currentProgress = 0;
      this.animating = false;
      this.compact = false;
      this.annH = 0;
      this.isMobile = false;

      // Clear any leftover manual inline styles
      const elementsToClear = header.querySelectorAll(
        '.vorneel-motion-logo__emblem, .vorneel-motion-logo__wordmark, .vorneel-motion-logo__stage, .vn-layout-left-classic, #navbar-mobile, .vn-mobile-nav-inner'
      );
      elementsToClear.forEach(el => {
        el.style.transform = '';
        el.style.height = '';
        el.style.minHeight = '';
        el.style.width = '';
      });

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('pageshow', this.onResize);

      this.updateSpacer();
      this.onScroll();
    }

    updateSpacer() {
      this.isMobile = window.matchMedia('(max-width: 991.98px)').matches;
      this.annH = this.announcement && this.announcement.offsetHeight > 0 ? this.announcement.offsetHeight : 0;
      const navHeight = this.header && this.header.offsetHeight > 0 ? this.header.offsetHeight : (this.isMobile ? 80 : 119);
      if (this.spacer) {
        this.spacer.style.height = `${this.annH + navHeight}px`;
      }
    }

    onResize() {
      this.updateSpacer();
      this.onScroll();
    }

    onScroll() {
      const scrollTop = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);

      // Continuous target progress from 0.0 to 1.0 based on scroll
      this.targetProgress = Math.min(1, Math.max(0, scrollTop / SCROLL_DISTANCE));

      // Seamless roll-away of announcement bar without any gaps or snaps:
      // #header-group translates up by min(scrollTop, announcementHeight).
      // The announcement bar and navbar stay firmly attached, eliminating all gaps!
      const annShift = Math.min(this.annH, scrollTop);
      if (this.headerGroup) {
        const hgTransform = `translate3d(0, -${annShift.toFixed(1)}px, 0)`;
        this.headerGroup.style.transform = hgTransform;
        this.headerGroup.style.setProperty('--vorneel-header-group-transform', hgTransform);
      }

      this.header.classList.toggle('is-sticky', scrollTop > 0);

      // Trigger animation frame loop
      if (!this.animating) {
        this.animating = true;
        this.tick();
      }
    }

    tick() {
      const diff = this.targetProgress - this.currentProgress;

      // Silk-smooth damping: cushions discrete wheel ticks into liquid gliding motion
      if (Math.abs(diff) < 0.002) {
        this.currentProgress = this.targetProgress;
        this.animating = false;
      } else {
        this.currentProgress += diff * 0.16;
        window.requestAnimationFrame(this.tick);
      }

      this.render(this.currentProgress);
    }

    render(rawP) {
      // Smoothstep easing for tactile, organic luxury curve: 3p^2 - 2p^3
      const p = rawP * rawP * (3 - 2 * rawP);
      const isMobile = this.isMobile;

      if (!isMobile) {
        // Desktop transforms:
        // Emblem glides left from 74px to 0px
        const emblemX = (74 * (1 - p)).toFixed(2);
        const emblemY = (6 * p).toFixed(2);
        const emblemScale = (1.0 - 0.08 * p).toFixed(3);

        // Wordmark glides UP and to the RIGHT:
        // X moves from 24px to 44px (slides 20px right!)
        // Y moves from 40px to 2px (slides 38px up!)
        const wordmarkX = (24 + 20 * p).toFixed(2);
        const wordmarkY = (40 - 38 * p).toFixed(2);
        const wordmarkScale = (1.0 - 0.05 * p).toFixed(3);

        const navH = (102 - 34 * p).toFixed(1);
        const stageH = (80 - 26 * p).toFixed(1);
        const stageW = (184 + 12 * p).toFixed(1);

        this.header.style.setProperty('--vorneel-emblem-transform', `translate3d(${emblemX}px, ${emblemY}px, 0) scale(${emblemScale})`);
        this.header.style.setProperty('--vorneel-wordmark-transform', `translate3d(${wordmarkX}px, ${wordmarkY}px, 0) scale(${wordmarkScale})`);
        this.header.style.setProperty('--vorneel-nav-height', `${navH}px`);
        this.header.style.setProperty('--vorneel-stage-height', `${stageH}px`);
        this.header.style.setProperty('--vorneel-stage-width', `${stageW}px`);
      } else {
        // Mobile transforms:
        const emblemX = (48 * (1 - p)).toFixed(2);
        const emblemY = (4 * p).toFixed(2);
        const emblemScale = (1.0 - 0.10 * p).toFixed(3);

        const wordmarkX = (16 + 10 * p).toFixed(2);
        const wordmarkY = (26 - 24 * p).toFixed(2);
        const wordmarkScale = (1.0 - 0.05 * p).toFixed(3);

        const mobileH = (80 - 22 * p).toFixed(1);
        const stageH = (56 - 16 * p).toFixed(1);
        const stageW = (120 + 4 * p).toFixed(1);

        this.header.style.setProperty('--vorneel-mobile-emblem-transform', `translate3d(${emblemX}px, ${emblemY}px, 0) scale(${emblemScale})`);
        this.header.style.setProperty('--vorneel-mobile-wordmark-transform', `translate3d(${wordmarkX}px, ${wordmarkY}px, 0) scale(${wordmarkScale})`);
        this.header.style.setProperty('--vorneel-mobile-height', `${mobileH}px`);
        this.header.style.setProperty('--vorneel-mobile-stage-height', `${stageH}px`);
        this.header.style.setProperty('--vorneel-mobile-stage-width', `${stageW}px`);
      }

      // Dynamic border radius: flat 0px on initial land, smoothly curves to rounded as user scrolls
      const maxRadius = isMobile ? 10 : 14;
      const radius = (maxRadius * p).toFixed(1);
      this.header.style.setProperty('--vorneel-nav-radius', `${radius}px`);

      // Compact state class
      const isCompact = rawP >= 0.95;
      if (isCompact !== this.compact) {
        this.compact = isCompact;
        this.header.classList.toggle('vorneel-nav-compact', isCompact);
        if (this.headerGroup) {
          this.headerGroup.classList.toggle('vorneel-header-compact', isCompact);
        }
      }
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pageshow', this.onResize);

      if (this.headerGroup) {
        this.headerGroup.style.transform = '';
        this.headerGroup.style.removeProperty('--vorneel-header-group-transform');
        this.headerGroup.classList.remove('vorneel-header-compact');
      }

      this.header.classList.remove('vorneel-nav-compact', 'is-sticky');

      this.header.style.removeProperty('--vorneel-emblem-transform');
      this.header.style.removeProperty('--vorneel-wordmark-transform');
      this.header.style.removeProperty('--vorneel-nav-height');
      this.header.style.removeProperty('--vorneel-stage-height');
      this.header.style.removeProperty('--vorneel-stage-width');
      this.header.style.removeProperty('--vorneel-mobile-emblem-transform');
      this.header.style.removeProperty('--vorneel-mobile-wordmark-transform');
      this.header.style.removeProperty('--vorneel-mobile-height');
      this.header.style.removeProperty('--vorneel-mobile-stage-height');
      this.header.style.removeProperty('--vorneel-mobile-stage-width');
      this.header.style.removeProperty('--vorneel-nav-radius');

      if (this.spacer) this.spacer.style.height = '';
    }
  }

  const mount = () => {
    if (window.vorneelLogoMotion) window.vorneelLogoMotion.destroy();
    const header = document.querySelector('#navbar-wrapper');
    window.vorneelLogoMotion = header ? new VorneelLogoMotion(header) : null;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', mount);
})();
